// Reusable hover-anchored floating tooltip for Ledger cells.
// Replaces in-cell metadata (document ref, dates, QB id, push button)
// with on-demand detail revealed when the user hovers the trigger.
//
// Pattern modeled after Linear / Stripe data-table cells:
//   - Cell shows the bare data point (typically a dollar amount)
//   - Hovering surfaces a small floating panel with supporting detail
//   - Tooltip is interactive (buttons, links inside it work) — small
//     mouse-leave delay so the user can move from trigger into the panel
//
// Uses position: fixed AND React portal to document.body. Two layers
// of escape because the prior fix (position: fixed alone) wasn't
// sufficient when the trigger lived inside a sticky-positioned cell:
//
//   - position: fixed controls the CONTAINING BLOCK (the tooltip
//     positions relative to the viewport instead of the parent).
//   - But position: sticky on an ancestor STILL creates a stacking
//     context — and stacking contexts follow DOM hierarchy regardless
//     of position values. The fixed tooltip's zIndex resolves WITHIN
//     the sticky ancestor's stacking context, so anything in the
//     page at higher z (sticky header at 5, etc.) renders above it.
//   - Portaling the tooltip to document.body moves its DOM node out
//     of the sticky cell entirely. It now lives in the root stacking
//     context where zIndex: 9999 means 9999 in absolute terms.
//
// PRODUCT cell (sticky-left) was the failure case that motivated the
// portal — all other lifecycle tooltips live in non-sticky <td>s and
// worked with just position:fixed. Portal fixes PRODUCT without
// changing behavior elsewhere.
//
// Trade-off: tooltip stays at its open-time viewport position if the
// user scrolls while hovering. Acceptable since the mouse-leave delay
// (120ms) closes the tooltip when the user moves their pointer away.

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

var TOOLTIP_LEAVE_DELAY = 120; // ms

export default function LedgerHoverTooltip(p) {
  var hs = useState(false); var hovered = hs[0]; var setHovered = hs[1];
  var rs = useState({ top: 0, anchorLeft: 0, anchorRight: 0 });
  var rect = rs[0]; var setRect = rs[1];
  var triggerRef = useRef(null);
  var timeoutRef = useRef(null);

  function compute() {
    if (!triggerRef.current) return;
    var r = triggerRef.current.getBoundingClientRect();
    setRect({
      top: r.bottom + 4,
      anchorLeft: r.left,
      anchorRight: r.right,
    });
  }

  function enter() {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    compute();
    setHovered(true);
  }
  function leave() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(function() { setHovered(false); }, TOOLTIP_LEAVE_DELAY);
  }

  useEffect(function() {
    return function() { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  if (!p.content) return p.children;

  // Anchor: default below-right of trigger. 'left' anchors to trigger's
  // left edge (useful when the trigger is near the right of the scroll
  // container and a right-anchored tooltip would clip off-screen).
  var anchorStyle;
  if (typeof window !== 'undefined') {
    if (p.position === 'left') {
      anchorStyle = { top: rect.top, left: rect.anchorLeft };
    } else {
      anchorStyle = { top: rect.top, right: Math.max(0, window.innerWidth - rect.anchorRight) };
    }
  } else {
    anchorStyle = { top: 0, left: 0 };
  }

  var tooltipNode = hovered ? (
    <div
      onMouseEnter={enter}
      onMouseLeave={leave}
      style={Object.assign({
        position: 'fixed',
        minWidth: p.width || 240,
        padding: '10px 12px',
        background: '#fff',
        border: '0.5px solid #D8D7D2',
        borderRadius: 4,
        boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
        zIndex: 9999,
        fontSize: 11,
        color: '#222',
        textAlign: 'left',
        whiteSpace: 'normal',
        fontFamily: 'inherit',
        cursor: 'auto',
      }, anchorStyle)}>
      {p.content}
    </div>
  ) : null;

  return (
    <span ref={triggerRef} style={{ display: 'inline-block' }}
          onMouseEnter={enter} onMouseLeave={leave}>
      {p.children}
      {tooltipNode && typeof document !== 'undefined'
        ? createPortal(tooltipNode, document.body)
        : null}
    </span>
  );
}
